import classNames from "classnames";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import { useI18n } from "../../hooks/useTranslations";
import { useCarTaskLogsApi } from "../../http/carTaskLogs/api";
import { CarTaskDone, CarTaskNotRequired, CarTaskToDo, getTaskState } from "../../http/carTaskLogs/types";
import { useCarTaskApi } from "../../http/carTasks/api";
import { CarTaskNoCarDependency, CarTaskState, getTaskStateColor, getTaskStateText } from "../../http/carTasks/types";
import { useDueDatesApi } from "../../http/dueDates/api";
import { UpsertDueDate } from "../../http/dueDates/types";
import { useAdminWorkflowsApi } from "../../http/workflow/api";
import { getIconProps } from "../../http/workflow/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { formatTimeStampNumeric } from "../../types/utils";
import DueDateFormNuovo, { DueDateFormBody } from "../dueDates/DueDateForm";
import SvgAddTask from "../icons/AddTask";
import SvgArrowDropDown from "../icons/ArrowDropDown";
import SvgAssignmentTurnedIn from "../icons/AssignmentTurnedIn";
import SvgCalendar from "../icons/CalendarMonth";
import SvgCancel from "../icons/Cancel";
import SvgCheckCircle from "../icons/CheckCircle";
import SvgDoNotDisturbOn from "../icons/DoNotDisturbOn";
import SvgEdit from "../icons/Edit";
import SvgWarning from "../icons/Warning";
import Button from "../ui/Button";
import CustomLink from "../ui/CustomLink";
import Header from "../ui/Header";
import Icon from "../ui/Icon";
import Inline from "../ui/Inline";
import { Action, getDeleteAction, getHistoryAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import Panel from "../ui/Panel";
import { State } from "../ui/State";
import Table from "../ui/Table";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import CheckWorkflowDialog from "../workflows/CheckWorkflowDialog";
import styles from "./CarTask.module.css";
import CarTasksHistory from "./CarTasksHistory";
import CreateCarTaskDialog from "./CreateCarTaskDialog";
import DefaultTasks from "./defaultTasks/DefaultTasks";
import DeleteCarTaskDialog from "./DeleteCarTaskDialog";
import EditCarTaskDialog from "./EditCarTaskDialog";

const CAR_TASK_FIELDS = [
  "id",
  "car.id",
  "car.vehicleIdentificationNumber",
  "car.currentPlateNumber",
  "car.lastPlateNumber",
  "state",
  "taskName",
  "createdAt",
  "lastModifiedBy",
  "lastModifiedAt",
  "driver.id",
  "driver.fullName",
  "defaultTask",
  "defaultTask.id",
  "defaultTask.name",
  "defaultTask.defaultDueDateType.id",
  "defaultTask.defaultDueDateType.name",
  "workflowState",
  "workflowId",
  "workflowCreatedAt",
] as const;
type CarTaskField = (typeof CAR_TASK_FIELDS)[number];
type CarTaskSearch = NestedPick<CarTaskNoCarDependency, CarTaskField>;

type CarTasksProps = {
  onlyToDo?: boolean;
};

const CarTasks: FC<CarTasksProps> = ({ onlyToDo }: CarTasksProps) => {
  const filters = useListFilters({
    isSearchable: true,
    prefix: "ct",
  });
  const workflowsApi = useAdminWorkflowsApi();
  const userSettings = useUserSettings();
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);

  const [itemToShowHistory, setItemToShowHistory] = useState<CarTaskSearch>();
  const [carTaskToEdit, setCarTaskToEdit] = useState<CarTaskSearch>();
  const [itemToDelete, setItemToDelete] = useState<CarTaskSearch>();
  const [showDefaultTasks, setShowDefaultTasks] = useState(false);
  const [itemToCreateDueDate, setItemToCreateDueDate] = useState<CarTaskSearch>();

  const [showNotifyDialog, setShowNotifyDialog] = useState<CarTaskSearch>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showCheckWorkflow, setShowCheckWorkflow] = useState<CarTaskSearch>();

  const [showListActionsMenu, setShowListActionsMenu] = useState<boolean>(false);
  const [showNewCarTask, setShowNewCarTask] = useState<{ isOpen: boolean; isWithDefault: boolean }>({
    isOpen: false,
    isWithDefault: false,
  });
  const { id: carId } = useParams<{ id: string }>();

  const i18n = useI18n();

  const carTasksApi = useCarTaskApi();
  const carTaskLogsApi = useCarTaskLogsApi();
  const dueDatesApi = useDueDatesApi();

  useEffect(() => {
    filters.setFilters("carTask.task.state", { operator: undefined, values: onlyToDo ? ["to-do"] : [] });
  }, [onlyToDo]);

  const query: PaginationQueryWithFields<CarTaskSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: CAR_TASK_FIELDS,
  };

  const onSubmitNewDueDate = async (formData: DueDateFormBody): Promise<void> => {
    if (itemToCreateDueDate != undefined) {
      const body: UpsertDueDate = {
        carId: itemToCreateDueDate.id,
        typeId: formData.type.id,
        date: formData.date,
        isSettled: formData.isSettled,
        visibleToUserId: formData.visibleToUser ? formData.driver?.id : undefined,
      };

      try {
        await dueDatesApi.insertAdminDueDate(body);
        dispatch(showSuccessNotification(i18n.t));
        setItemToCreateDueDate(undefined);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      }
    }
  };

  const result = carTasksApi.useAdminCarTasks(
    query,
    aborter.current.signal,
    carId,
    !!userSettings.permissions.carTasks.read,
  );
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: carTasksApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const toggle = (carTask: CarTaskSearch, state: CarTaskState) => {
    carTaskLogsApi
      .insertAdminCarTaskLog(
        carTask.id,
        {
          state: state,
        },
        carTask.car.id,
      )
      .catch((err) => showErrorNotification(i18n, err));
  };

  const notifyDriver = async (item: CarTaskSearch) => {
    setIsSubmitting(true);
    try {
      await workflowsApi.createWorkflow(item.id, carId!);
      dispatch(showSuccessNotification(i18n.t));
      setShowNotifyDialog(undefined);
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const rowActions = (item: CarTaskSearch): Action[] => [
    {
      title: i18n.t.common.setAsTodo,
      onClick: () => toggle(item, CarTaskToDo),
      isDisabled:
        item.state === CarTaskToDo ||
        !userSettings.permissions.carTasks.insert ||
        !userSettings.permissions.carTasks.update,
      icon: SvgCancel,
      color: "red",
    },
    {
      title: i18n.t.common.setAsDone,
      onClick: () => toggle(item, CarTaskDone),
      isDisabled:
        item.state === CarTaskDone ||
        !userSettings.permissions.carTasks.insert ||
        !userSettings.permissions.carTasks.update,
      icon: SvgCheckCircle,
      color: "green",
    },
    {
      title: i18n.t.common.setAsNotRequired,
      onClick: () => toggle(item, CarTaskNotRequired),
      isDisabled:
        item.state === CarTaskNotRequired ||
        !userSettings.permissions.carTasks.insert ||
        !userSettings.permissions.carTasks.update,
      icon: SvgDoNotDisturbOn,
      color: "blue",
    },
    getHistoryAction(i18n, () => setItemToShowHistory(item), true),
    ...(userSettings.permissions.dueDates.insert
      ? [
          {
            title: i18n.t.common.addDueDate,
            onClick: () => setItemToCreateDueDate(item),
            icon: SvgCalendar,
          },
        ]
      : []),
    ...(userSettings.permissions.carTasks.update
      ? [
          {
            title: i18n.t.common.rename,
            onClick: () => setCarTaskToEdit(item),
            icon: SvgEdit,
          },
        ]
      : []),
    {
      title: i18n.t.common.notifyDriver,
      onClick: () => {
        if (!item.workflowState) {
          setShowNotifyDialog(item);
        }
      },
      visible: item.defaultTask?.id == "notify-stickers" && !item.workflowState,
      icon: SvgWarning,
      isDisabled: !item.driver,
    },
    {
      title: i18n.t.common.checkWorkflow,
      icon: SvgAssignmentTurnedIn,
      onClick: () => {
        setShowCheckWorkflow(item);
      },
      visible: item.defaultTask?.id == "notify-stickers" && !!item.workflowState,
      isDisabled: !item.driver,
    },
    ...(userSettings.permissions.carTasks.delete ? [getDeleteAction(i18n, () => setItemToDelete(item), true)] : []),
  ];

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getIcon = (item: CarTaskSearch) => {
    if (!item.workflowState || !item.workflowCreatedAt) return undefined;

    const props = getIconProps(i18n.t, item.workflowState, item.workflowCreatedAt);

    return (
      <Inline title={props.title} onClick={() => setShowCheckWorkflow(item)}>
        <Icon glyph={props.glyph} className={classNames(styles.icon)} color={props.color} />
      </Inline>
    );
  };
  const menuRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(menuRef, () => setShowListActionsMenu(false));

  const content = (
    <>
      <Table<CarTaskSearch>
        filters={filters}
        buttons={{
          main: userSettings.permissions.carTasks.insert && (
            <div ref={menuRef} className={styles["menu-container"]}>
              <Button
                glyph={SvgArrowDropDown}
                buttonProps={{ onClick: () => setShowListActionsMenu(!showListActionsMenu) }}
              />
              <div className={classNames(styles.menu, { [styles.visible]: showListActionsMenu })}>
                <div
                  className={styles.action}
                  onClick={() => {
                    setShowNewCarTask({ isOpen: true, isWithDefault: false });
                    setShowListActionsMenu(false);
                  }}
                >
                  <Icon glyph={SvgAddTask} className={styles.icon} />
                  {i18n.t.common.addCustomCarTask}
                </div>
                <div
                  className={styles.action}
                  onClick={() => {
                    setShowNewCarTask({ isOpen: true, isWithDefault: false });
                    setShowListActionsMenu(false);
                  }}
                >
                  <Icon glyph={SvgAssignmentTurnedIn} className={styles.icon} />
                  {i18n.t.common.addDefaultCarTask}
                </div>
              </div>
            </div>
          ),
          secondary: userSettings.permissions.defaultTask.read && (
            <Button
              buttonProps={{
                onClick: () => setShowDefaultTasks(true),
              }}
            >
              {i18n.t.common.manageDefaultTasks}
            </Button>
          ),
        }}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        head={
          <>
            <Table.Th
              title={i18n.t.tasks.singular}
              fieldName="carTask.task.name"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.common.state}
              fieldName="carTask.task.state"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
              formatter={(value) => getTaskState(i18n.t).find((x) => x.value === value)?.label}
            />
            {!carId && (
              <Table.Th
                title={i18n.t.common.car}
                fieldName="carTask.car.plateNumber"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
            )}
            <Table.Th
              title={i18n.t.drivers.singular}
              fieldName="carTask.driver"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.common.modifiedBy}
              fieldName="carTask.user"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th title={i18n.t.common.time} fieldName="carTask.time" order={orderOptions} />
            <Table.Th title={i18n.t.common.createdAt} fieldName="carTask.createdAt" order={orderOptions} />
            <Table.Th
              title="WF"
              fieldName="carTask.workflow.state"
              order={orderOptions}
              filters={filterOptions}
              filter={TextColumnFilter}
            />
            <th align="left"></th>
          </>
        }
        renderRow={(item) => (
          <>
            <td>{item.taskName}</td>
            <td>
              <State
                text={item.state ? getTaskStateText(i18n.t, item.state) : getTaskStateText(i18n.t, undefined)}
                color={getTaskStateColor(item.state)}
              />
            </td>
            {!carId && (
              <td>
                {!!userSettings.permissions.cars.read ? (
                  <CustomLink to={`/admin/cars/${item.car.id}`}>
                    {item.car.currentPlateNumber || item.car.lastPlateNumber}
                  </CustomLink>
                ) : (
                  <>{item.car.currentPlateNumber || item.car.lastPlateNumber}</>
                )}
              </td>
            )}
            <td>
              {!!userSettings.permissions.users.read ? (
                <CustomLink to={`/admin/users/${item.driver?.id}`}>{item.driver?.fullName}</CustomLink>
              ) : (
                item.driver?.fullName
              )}
            </td>
            <td>
              {!!userSettings.permissions.users.read ? (
                <CustomLink to={`/admin/users/${item.lastModifiedBy?.id}`}>{item.lastModifiedBy?.fullName}</CustomLink>
              ) : (
                item.lastModifiedBy?.fullName
              )}
            </td>
            <td>{!!item.lastModifiedAt && formatTimeStampNumeric(item.lastModifiedAt, i18n.language)}</td>
            <td>{formatTimeStampNumeric(item.createdAt, i18n.language)}</td>
            <td>{getIcon(item)}</td>
          </>
        )}
        rowActions={rowActions}
        total={data?.total}
        items={data?.items}
        error={result.error}
      />

      {!!itemToShowHistory && (
        <CarTasksHistory
          carTaskId={itemToShowHistory.id}
          taskName={itemToShowHistory.taskName}
          onClose={() => setItemToShowHistory(undefined)}
        />
      )}

      {showDefaultTasks && <DefaultTasks onClose={() => setShowDefaultTasks(false)} />}

      {!!carTaskToEdit && (
        <EditCarTaskDialog carTaskId={carTaskToEdit.id} onClose={() => setCarTaskToEdit(undefined)} carId={carId} />
      )}

      {!!showNewCarTask.isOpen && (
        <CreateCarTaskDialog
          onClose={() => setShowNewCarTask({ isOpen: false, isWithDefault: false })}
          carId={carId}
          isWithDefaultTask={showNewCarTask.isWithDefault}
        />
      )}

      {!!itemToDelete && (
        <DeleteCarTaskDialog
          carTaskId={itemToDelete.id}
          carId={itemToDelete.car.id}
          onClose={() => setItemToDelete(undefined)}
          workflowId={itemToDelete.workflowId}
        />
      )}

      {!!itemToCreateDueDate && !!userSettings.permissions.dueDates.insert && (
        <ModalDialog title={i18n.t.dueDates.new} onClose={() => setItemToCreateDueDate(undefined)}>
          <DueDateFormNuovo
            onSubmit={onSubmitNewDueDate}
            defaultTask={itemToCreateDueDate.defaultTask}
            carId={itemToCreateDueDate.car.id}
          />
        </ModalDialog>
      )}

      {!!showNotifyDialog && (
        <ModalDialog
          isSubmitting={isSubmitting}
          onClose={() => {
            setShowNotifyDialog(undefined);
          }}
          title={i18n.t.common.notifyDriver}
          actions={[
            {
              title: i18n.t.common.send,
              onClick: () => notifyDriver(showNotifyDialog),
            },
          ]}
        >
          <div>{i18n.t.common.emailSendToDriver}</div>
        </ModalDialog>
      )}

      {!!showCheckWorkflow && showCheckWorkflow.workflowId && (
        <CheckWorkflowDialog
          carId={showCheckWorkflow.car.id}
          onClose={() => setShowCheckWorkflow(undefined)}
          workflowId={showCheckWorkflow.workflowId}
        />
      )}
    </>
  );

  return carId ? (
    content
  ) : (
    <>
      <Header title={i18n.t.carTasks.plural} />
      <Panel>{content}</Panel>
    </>
  );
};

export default CarTasks;
